import { useEffect, useRef, useCallback } from 'react';

// BSI-Empfehlung für medizinische Software: max. 15 Minuten Inaktivität
const TIMEOUT_MS = 15 * 60 * 1000;

// 2 Minuten vor Ablauf: Warn-Toast anzeigen
const WARNING_BEFORE_MS = 2 * 60 * 1000;

// Ereignisse die als "aktiv" gelten
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

interface Options {
  onTimeout: () => void;
  onWarning: () => void;
  enabled: boolean;
}

export function useInactivityTimeout({ onTimeout, onWarning, enabled }: Options) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
  }, []);

  const resetTimers = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    warnedRef.current = false;

    // Warning-Timer: 13 Minuten nach letzter Aktivität
    warningRef.current = setTimeout(() => {
      if (!warnedRef.current) {
        warnedRef.current = true;
        onWarning();
      }
    }, TIMEOUT_MS - WARNING_BEFORE_MS);

    // Logout-Timer: 15 Minuten nach letzter Aktivität
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, TIMEOUT_MS);
  }, [enabled, clearTimers, onTimeout, onWarning]);

  useEffect(() => {
    if (!enabled) return;

    resetTimers();

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetTimers, { passive: true });
    });

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, resetTimers);
      });
    };
  }, [enabled, resetTimers, clearTimers]);
}
